import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Box } from "@mui/material";

import LoadingShow from "components/common/LoadingShow";
import {
  fetchMaintenanceConfig,
  type MaintenanceConfig,
} from "lib/maintenanceMode";
import Maintenance from "views/Maintenance";

const CHECK_INTERVAL_MS = 30_000;
const REQUEST_TIMEOUT_MS = 5_000;
const AVAILABLE_CONFIG: MaintenanceConfig = { enabled: false };

type MaintenanceGateProps = {
  children: ReactNode;
};

const MaintenanceGate = ({ children }: MaintenanceGateProps) => {
  const [config, setConfig] = useState<MaintenanceConfig | null>(null);
  const [checking, setChecking] = useState(false);
  const mountedRef = useRef(false);
  const requestRef = useRef<AbortController | null>(null);

  const checkStatus = useCallback(async () => {
    if (requestRef.current) {
      return;
    }

    const controller = new AbortController();
    requestRef.current = controller;
    setChecking(true);
    const timeout = window.setTimeout(
      () => controller.abort(),
      REQUEST_TIMEOUT_MS
    );

    const nextConfig = await fetchMaintenanceConfig(controller.signal);

    window.clearTimeout(timeout);
    requestRef.current = null;

    if (!mountedRef.current) {
      return;
    }

    setChecking(false);
    if (nextConfig) {
      setConfig(nextConfig);
    } else {
      // Do not dismiss an active maintenance screen on a transient network error.
      setConfig((current) => current || AVAILABLE_CONFIG);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    checkStatus();

    const interval = window.setInterval(checkStatus, CHECK_INTERVAL_MS);
    const checkWhenVisible = () => {
      if (document.visibilityState === "visible") {
        checkStatus();
      }
    };

    window.addEventListener("focus", checkStatus);
    document.addEventListener("visibilitychange", checkWhenVisible);

    return () => {
      mountedRef.current = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", checkStatus);
      document.removeEventListener("visibilitychange", checkWhenVisible);
      requestRef.current?.abort();
      requestRef.current = null;
    };
  }, [checkStatus]);

  if (!config) {
    // Keep the app unmounted without announcing maintenance before the check.
    return (
      <Box
        component="main"
        height="100vh"
        bgcolor="background.default"
        aria-busy
      >
        <LoadingShow />
      </Box>
    );
  }

  if (config.enabled) {
    return (
      <Maintenance config={config} checking={checking} onRetry={checkStatus} />
    );
  }

  return <>{children}</>;
};

export default MaintenanceGate;
